import React, { useCallback, useContext, useMemo, useState, useEffect } from 'react';
import {
	Button,
	ButtonBase,
	Card,
	CardActionArea,
	Grid,
	IconButton,
	InputAdornment,
	Paper,
	TextField,
	Typography,
	makeStyles
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import ArrowBackRoundedIcon from '@material-ui/icons/ArrowBackRounded';
import ClearRoundedIcon from '@material-ui/icons/ClearRounded';
import ErrorOutlineRoundedIcon from '@material-ui/icons/ErrorOutlineRounded';
import PlayArrowRoundedIcon from '@material-ui/icons/PlayArrowRounded';
import ReplayRoundedIcon from '@material-ui/icons/ReplayRounded';
import SearchRoundedIcon from '@material-ui/icons/SearchRounded';
import VideoLibraryRoundedIcon from '@material-ui/icons/VideoLibraryRounded';
import { useSnackbar } from 'notistack';
import JString from '@easytech-international-sdn-bhd/jstring';

import AppContext from '../../Services/AppContext';
import PersistantData from '../../Services/PersistantData';
import API_Service, { axiosDefaultConfig } from '../../Services/API_Service';
import { req_module_setting } from '../../Helper/Constants';
import { getSetting } from '../../Helper/Helper';

const RAIL_WIDTH = 340;

const useStyles = makeStyles((theme) => ({
	root: {
		width: '100%',
		paddingBottom: theme.spacing(4)
	},
	hero: {
		position: 'relative',
		overflow: 'hidden',
		marginBottom: theme.spacing(2),
		padding: theme.spacing(2, 2.5),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.spacing(2),
		background: theme.palette.type === 'dark'
			? 'linear-gradient(135deg, rgba(254,242,0,0.16) 0%, rgba(254,242,0,0.03) 48%, rgba(255,255,255,0.02) 100%)'
			: 'linear-gradient(135deg, rgba(254,242,0,0.34) 0%, rgba(254,242,0,0.08) 48%, rgba(255,255,255,0.86) 100%)'
	},
	heroContent: {
		position: 'relative',
		zIndex: 1,
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap'
	},
	heroIcon: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: theme.spacing(6),
		height: theme.spacing(6),
		marginRight: theme.spacing(2),
		borderRadius: theme.spacing(1.5),
		backgroundColor: '#FEF200',
		color: '#111'
	},
	heroTitle: {
		fontWeight: 700,
		letterSpacing: '-0.02em'
	},
	heroSubtitle: {
		marginTop: theme.spacing(0.5),
		color: theme.palette.text.secondary
	},
	heroSearch: {
		display: 'flex',
		alignItems: 'center',
		flex: 1,
		justifyContent: 'flex-end',
		marginLeft: 'auto',
		paddingLeft: theme.spacing(2)
	},
	searchField: {
		flex: 1,
		minWidth: 240,
		maxWidth: 520,
		'& .MuiOutlinedInput-root': {
			borderRadius: theme.spacing(1.25),
			backgroundColor: theme.palette.background.paper
		}
	},
	resultCount: {
		marginLeft: theme.spacing(2),
		minWidth: theme.spacing(10),
		color: theme.palette.text.secondary,
		fontWeight: 500,
		textAlign: 'right'
	},
	card: {
		height: '100%',
		overflow: 'hidden',
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.spacing(1.75),
		backgroundColor: theme.palette.background.paper,
		transition: theme.transitions.create(['transform', 'box-shadow', 'border-color'], {
			duration: 220,
			easing: theme.transitions.easing.easeOut
		}),
		'&:hover': {
			transform: 'translateY(-5px)',
			borderColor: 'rgba(254, 242, 0, 0.78)',
			boxShadow: theme.shadows[8]
		},
		'&:focus-within': {
			boxShadow: '0 0 0 3px rgba(254, 242, 0, 0.5)'
		},
		'&:hover $thumbnail': {
			transform: 'scale(1.045)'
		},
		'&:hover $playButton': {
			transform: 'scale(1.08)'
		},
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
			'&:hover': {
				transform: 'none'
			},
			'&:hover $thumbnail': {
				transform: 'none'
			},
			'&:hover $playButton': {
				transform: 'none'
			}
		}
	},
	cardAction: {
		display: 'block',
		height: '100%',
		textAlign: 'left'
	},
	thumbnailArea: {
		position: 'relative',
		height: 0,
		paddingTop: '56.25%',
		overflow: 'hidden',
		background: theme.palette.type === 'dark'
			? 'linear-gradient(135deg, #2b2b24, #111)'
			: 'linear-gradient(135deg, #fffbd1, #eee68b)'
	},
	thumbnail: {
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		objectFit: 'cover',
		transition: theme.transitions.create('transform', {
			duration: 320,
			easing: theme.transitions.easing.easeOut
		}),
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none'
		}
	},
	thumbnailFallback: {
		position: 'absolute',
		top: 0,
		left: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '100%',
		height: '100%',
		color: theme.palette.type === 'dark' ? '#FEF200' : '#2d2b00'
	},
	fallbackIcon: {
		fontSize: theme.spacing(7),
		opacity: 0.75
	},
	thumbnailScrim: {
		position: 'absolute',
		left: 0,
		right: 0,
		bottom: 0,
		height: '58%',
		background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.54) 100%)'
	},
	playButton: {
		position: 'absolute',
		left: theme.spacing(2),
		bottom: theme.spacing(1.5),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: theme.spacing(5.5),
		height: theme.spacing(5.5),
		borderRadius: '50%',
		backgroundColor: '#FEF200',
		color: '#111',
		boxShadow: '0 8px 20px rgba(0,0,0,0.28)',
		transition: theme.transitions.create('transform', {
			duration: theme.transitions.duration.shorter
		}),
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none'
		}
	},
	cardContent: {
		display: 'flex',
		alignItems: 'center',
		minHeight: theme.spacing(9),
		padding: theme.spacing(1.75, 2)
	},
	cardTitle: {
		display: '-webkit-box',
		overflow: 'hidden',
		fontWeight: 600,
		lineHeight: 1.4,
		WebkitBoxOrient: 'vertical',
		WebkitLineClamp: 2
	},
	statePanel: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		minHeight: theme.spacing(35),
		padding: theme.spacing(4),
		border: `1px dashed ${theme.palette.divider}`,
		borderRadius: theme.spacing(2),
		textAlign: 'center',
		backgroundColor: theme.palette.background.paper
	},
	stateIcon: {
		marginBottom: theme.spacing(1.5),
		fontSize: theme.spacing(7),
		color: theme.palette.text.secondary
	},
	stateMessage: {
		marginTop: theme.spacing(0.75),
		color: theme.palette.text.secondary
	},
	retryButton: {
		marginTop: theme.spacing(2),
		backgroundColor: '#FEF200',
		color: '#111',
		'&:hover': {
			backgroundColor: '#e7dc00'
		}
	},
	skeletonCard: {
		borderRadius: theme.spacing(1.75)
	},
	'@keyframes theaterIn': {
		from: { opacity: 0, transform: 'translateY(8px)' },
		to: { opacity: 1, transform: 'none' }
	},
	'@keyframes eqBar': {
		'0%': { transform: 'scaleY(0.35)' },
		'50%': { transform: 'scaleY(1)' },
		'100%': { transform: 'scaleY(0.35)' }
	},
	theater: {
		animation: '$theaterIn 240ms ease-out both',
		'@media (prefers-reduced-motion: reduce)': {
			animation: 'none'
		}
	},
	theaterHead: {
		display: 'flex',
		alignItems: 'center',
		marginBottom: theme.spacing(1.5)
	},
	backButton: {
		marginRight: theme.spacing(1),
		transition: theme.transitions.create(['transform', 'background-color'], {
			duration: theme.transitions.duration.shorter
		}),
		'&:hover': {
			transform: 'translateX(-2px)'
		},
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
			'&:hover': {
				transform: 'none'
			}
		}
	},
	theaterTitle: {
		flex: 1,
		minWidth: 0,
		fontWeight: 700,
		letterSpacing: '-0.01em',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap'
	},
	theaterBody: {
		position: 'relative',
		paddingRight: RAIL_WIDTH + theme.spacing(2),
		[theme.breakpoints.down('sm')]: {
			paddingRight: 0
		}
	},
	playerShell: {
		position: 'relative',
		height: 0,
		paddingTop: '56.25%',
		overflow: 'hidden',
		borderRadius: theme.spacing(2),
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: '#000'
	},
	playerIframe: {
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		border: 0
	},
	rail: {
		position: 'absolute',
		top: 0,
		right: 0,
		bottom: 0,
		width: RAIL_WIDTH,
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.spacing(2),
		backgroundColor: theme.palette.background.paper,
		[theme.breakpoints.down('sm')]: {
			position: 'static',
			width: '100%',
			maxHeight: 380,
			marginTop: theme.spacing(2)
		}
	},
	railHeader: {
		padding: theme.spacing(1.5, 1.5, 1.25),
		borderBottom: `1px solid ${theme.palette.divider}`
	},
	railSearchField: {
		width: '100%',
		'& .MuiOutlinedInput-root': {
			borderRadius: theme.spacing(1.25)
		}
	},
	railList: {
		flex: 1,
		minHeight: 0,
		overflowY: 'auto',
		padding: theme.spacing(1),
		scrollbarWidth: 'thin',
		scrollbarColor: theme.palette.type === 'dark'
			? 'rgba(255,255,255,0.26) transparent'
			: 'rgba(0,0,0,0.38) transparent',
		'&::-webkit-scrollbar': {
			width: 10
		},
		'&::-webkit-scrollbar-track': {
			backgroundColor: 'transparent'
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.26)' : 'rgba(0,0,0,0.38)',
			borderRadius: 8,
			border: '3px solid transparent',
			backgroundClip: 'padding-box'
		},
		'&::-webkit-scrollbar-thumb:hover': {
			backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.42)' : 'rgba(0,0,0,0.55)'
		}
	},
	railItem: {
		width: '100%',
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'flex-start',
		textAlign: 'left',
		padding: theme.spacing(0.75),
		marginBottom: theme.spacing(0.5),
		borderRadius: theme.spacing(1.25),
		transition: theme.transitions.create('background-color', {
			duration: theme.transitions.duration.shortest
		}),
		'&:hover': {
			backgroundColor: theme.palette.action.hover
		},
		'&.Mui-focusVisible': {
			boxShadow: '0 0 0 3px rgba(254, 242, 0, 0.5)'
		},
		'&:last-child': {
			marginBottom: 0
		},
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none'
		}
	},
	railItemActive: {
		boxShadow: 'inset 0 0 0 2px #FEF200',
		backgroundColor: theme.palette.type === 'dark' ? 'rgba(254,242,0,0.10)' : 'rgba(254,242,0,0.22)',
		'&:hover': {
			backgroundColor: theme.palette.type === 'dark' ? 'rgba(254,242,0,0.14)' : 'rgba(254,242,0,0.28)'
		}
	},
	railThumb: {
		position: 'relative',
		width: 112,
		height: 63,
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
		marginRight: theme.spacing(1.25),
		borderRadius: theme.spacing(1),
		background: theme.palette.type === 'dark'
			? 'linear-gradient(135deg, #2b2b24, #111)'
			: 'linear-gradient(135deg, #fffbd1, #eee68b)',
		color: theme.palette.type === 'dark' ? '#FEF200' : '#2d2b00'
	},
	railThumbImg: {
		display: 'block',
		width: '100%',
		height: '100%',
		objectFit: 'cover'
	},
	railFallbackIcon: {
		fontSize: theme.spacing(4),
		opacity: 0.75
	},
	railItemTitle: {
		display: '-webkit-box',
		overflow: 'hidden',
		fontWeight: 600,
		fontSize: 13,
		lineHeight: 1.4,
		paddingTop: theme.spacing(0.25),
		WebkitBoxOrient: 'vertical',
		WebkitLineClamp: 2
	},
	railEmpty: {
		padding: theme.spacing(2),
		textAlign: 'center'
	},
	eqOverlay: {
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: 'rgba(0,0,0,0.45)',
		'& span': {
			width: 3,
			height: 14,
			borderRadius: 1,
			backgroundColor: '#FEF200',
			transformOrigin: 'bottom',
			animation: '$eqBar 900ms ease-in-out infinite'
		},
		'& span + span': {
			marginLeft: 3
		},
		'& span:nth-child(2)': {
			animationDelay: '180ms'
		},
		'& span:nth-child(3)': {
			animationDelay: '360ms'
		},
		'@media (prefers-reduced-motion: reduce)': {
			'& span': {
				animation: 'none',
				transform: 'scaleY(0.6)'
			}
		}
	},
	[theme.breakpoints.down('xs')]: {
		hero: {
			padding: theme.spacing(2)
		},
		heroIcon: {
			width: theme.spacing(5),
			height: theme.spacing(5)
		},
		heroSearch: {
			width: '100%',
			flexWrap: 'wrap',
			marginLeft: 0,
			marginTop: theme.spacing(1.5),
			paddingLeft: 0
		},
		searchField: {
			width: '100%'
		},
		resultCount: {
			marginTop: theme.spacing(1),
			marginLeft: 0,
			textAlign: 'left'
		}
	}
}));

export const extractYouTubeVideoId = (url) => {
	if (typeof url !== 'string' || url.trim() === '') return '';

	try {
		const parsedUrl = new URL(url);
		const hostname = parsedUrl.hostname.toLowerCase().replace(/^www\./, '');
		const pathParts = parsedUrl.pathname.split('/').filter(Boolean);

		if (hostname === 'youtu.be') return pathParts[0] || '';
		if (hostname === 'youtube.com' || hostname === 'youtube-nocookie.com' || hostname.endsWith('.youtube.com')) {
			if (pathParts[0] === 'embed' || pathParts[0] === 'shorts') return pathParts[1] || '';
			return parsedUrl.searchParams.get('v') || '';
		}
	} catch (error) {
		return '';
	}

	return '';
};

export const normalizeTutorials = (tutorialConfig) => {
	if (!tutorialConfig || typeof tutorialConfig !== 'object' || Array.isArray(tutorialConfig)) return [];

	return Object.entries(tutorialConfig)
		.filter(([title, url]) => typeof title === 'string' && title.trim() !== '' && typeof url === 'string' && url.trim() !== '')
		.map(([title, url]) => {
			const videoId = extractYouTubeVideoId(url);
			return {
				title,
				url,
				thumbnailUrl: videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : ''
			};
		});
};

export const buildAutoplayUrl = (url) => {
	if (typeof url !== 'string' || url.trim() === '') return '';
	return `${url}${url.includes('?') ? '&' : '?'}autoplay=1&rel=0`;
};

function TutorialCard({ tutorial, language, onSelect }) {
	const classes = useStyles();
	const [thumbnailFailed, setThumbnailFailed] = useState(false);
	const showThumbnail = tutorial.thumbnailUrl && !thumbnailFailed;

	const handleSelect = () => {
		onSelect(tutorial);
	};

	const handleThumbnailError = () => {
		setThumbnailFailed(true);
	};

	return (
		<Card elevation={0} className={classes.card}>
			<CardActionArea
				className={classes.cardAction}
				onClick={handleSelect}
				aria-label={`${language.tutorial_watch}: ${tutorial.title}`}
			>
				<div className={classes.thumbnailArea}>
					{showThumbnail ? (
						<img
							className={classes.thumbnail}
							src={tutorial.thumbnailUrl}
							alt=""
							loading="lazy"
							onError={handleThumbnailError}
						/>
					) : (
						<div className={classes.thumbnailFallback}>
							<VideoLibraryRoundedIcon className={classes.fallbackIcon} />
						</div>
					)}
					<div className={classes.thumbnailScrim} />
					<div className={classes.playButton}>
						<PlayArrowRoundedIcon />
					</div>
				</div>
				<div className={classes.cardContent}>
					<Typography variant="subtitle1" className={classes.cardTitle}>
						{tutorial.title}
					</Typography>
				</div>
			</CardActionArea>
		</Card>
	);
}

function TutorialRailItem({ tutorial, language, isActive, onSelect }) {
	const classes = useStyles();
	const [thumbnailFailed, setThumbnailFailed] = useState(false);
	const showThumbnail = tutorial.thumbnailUrl && !thumbnailFailed;

	const handleSelect = () => {
		onSelect(tutorial);
	};

	const handleThumbnailError = () => {
		setThumbnailFailed(true);
	};

	return (
		<ButtonBase
			focusRipple
			className={`${classes.railItem} ${isActive ? classes.railItemActive : ''}`}
			onClick={handleSelect}
			aria-label={`${language.tutorial_watch}: ${tutorial.title}`}
			aria-current={isActive ? 'true' : undefined}
		>
			<span className={classes.railThumb}>
				{showThumbnail ? (
					<img
						className={classes.railThumbImg}
						src={tutorial.thumbnailUrl}
						alt=""
						loading="lazy"
						onError={handleThumbnailError}
					/>
				) : (
					<VideoLibraryRoundedIcon className={classes.railFallbackIcon} />
				)}
				{isActive && (
					<span className={classes.eqOverlay}>
						<span /><span /><span />
					</span>
				)}
			</span>
			<Typography component="span" variant="body2" className={classes.railItemTitle}>
				{tutorial.title}
			</Typography>
		</ButtonBase>
	);
}

export default function Tutorials() {
	const classes = useStyles();
	const [gState] = useContext(AppContext);
	const { enqueueSnackbar } = useSnackbar();
	const singleton = PersistantData.getInstance();
	const language = gState.translation || singleton.getlang() || {};
	const [tutorialList, setTutorialsList] = useState([]);
	const [selectedTutorial, setSelectedTutorial] = useState(null);
	const [searchQuery, setSearchQuery] = useState('');
	const [isLoading, setIsLoading] = useState(true);
	const [hasLoadError, setHasLoadError] = useState(false);

	const loadTutorials = useCallback(() => {
		if (!JString(gState.client).isValid()) {
			setTutorialsList([]);
			setIsLoading(false);
			return;
		}

		setIsLoading(true);
		setHasLoadError(false);
		API_Service.get(req_module_setting, axiosDefaultConfig(gState.client))
			.then(({ data }) => {
				const { result, extraData, message } = data;
				if (Number(result) === 1) {
					const tutorialConfig = getSetting(extraData, 'tutorial', {});
					setTutorialsList(normalizeTutorials(tutorialConfig));
					return;
				}

				setTutorialsList([]);
				setHasLoadError(true);
				enqueueSnackbar(message || language.tutorial_load_failed, { variant: 'error' });
			})
			.catch(() => {
				setTutorialsList([]);
				setHasLoadError(true);
				enqueueSnackbar(language.tutorial_load_failed, { variant: 'error' });
			})
			.finally(() => setIsLoading(false));
	}, [enqueueSnackbar, gState.client, language.tutorial_load_failed]);

	useEffect(() => {
		loadTutorials();
	}, [loadTutorials]);

	useEffect(() => {
		if (!selectedTutorial) return undefined;
		const handleKeyDown = (event) => {
			if (event.key === 'Escape') {
				setSelectedTutorial(null);
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [selectedTutorial]);

	const filteredTutorials = useMemo(() => {
		const query = searchQuery.trim().toLocaleLowerCase();
		if (!query) return tutorialList;
		return tutorialList.filter((tutorial) => tutorial.title.toLocaleLowerCase().includes(query));
	}, [searchQuery, tutorialList]);

	const handleSearchChange = (event) => {
		setSearchQuery(event.target.value);
	};

	const handleClearSearch = () => {
		setSearchQuery('');
	};

	const handleSelectTutorial = (tutorial) => {
		setSelectedTutorial(tutorial);
	};

	const handleCloseVideo = () => {
		setSelectedTutorial(null);
	};

	const handleRetry = () => {
		loadTutorials();
	};

	const searchInputProps = {
		startAdornment: (
			<InputAdornment position="start">
				<SearchRoundedIcon color="action" />
			</InputAdornment>
		),
		endAdornment: searchQuery ? (
			<InputAdornment position="end">
				<IconButton size="small" onClick={handleClearSearch} aria-label={language.clear}>
					<ClearRoundedIcon fontSize="small" />
				</IconButton>
			</InputAdornment>
		) : null
	};

	return (
		<div className={classes.root}>
			{selectedTutorial ? (
				<div className={classes.theater}>
					<div className={classes.theaterHead}>
						<IconButton className={classes.backButton} onClick={handleCloseVideo} aria-label={language.close}>
							<ArrowBackRoundedIcon />
						</IconButton>
						<Typography variant="h6" className={classes.theaterTitle}>
							{selectedTutorial.title}
						</Typography>
					</div>
					<div className={classes.theaterBody}>
						<div className={classes.playerShell}>
							<iframe
								key={selectedTutorial.url}
								className={classes.playerIframe}
								src={buildAutoplayUrl(selectedTutorial.url)}
								title={selectedTutorial.title}
								frameBorder="0"
								allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
								allowFullScreen
							/>
						</div>
						<div className={classes.rail}>
							<div className={classes.railHeader}>
								<TextField
									className={classes.railSearchField}
									variant="outlined"
									size="small"
									value={searchQuery}
									onChange={handleSearchChange}
									placeholder={language.tutorial_search_placeholder}
									inputProps={{ 'aria-label': language.tutorial_search_placeholder }}
									InputProps={searchInputProps}
								/>
							</div>
							<div className={classes.railList}>
								{filteredTutorials.map((tutorial) => (
									<TutorialRailItem
										key={`${tutorial.title}-${tutorial.url}`}
										tutorial={tutorial}
										language={language}
										isActive={tutorial.title === selectedTutorial.title && tutorial.url === selectedTutorial.url}
										onSelect={handleSelectTutorial}
									/>
								))}
								{filteredTutorials.length === 0 && (
									<div className={classes.railEmpty}>
										<Typography variant="body2" color="textSecondary">
											{language.no_matches_found}
										</Typography>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			) : (
				<React.Fragment>
					<Paper elevation={0} className={classes.hero}>
						<div className={classes.heroContent}>
							<div className={classes.heroIcon}>
								<VideoLibraryRoundedIcon />
							</div>
							<div>
								<Typography variant="h5" className={classes.heroTitle}>
									{language.tutorials}
								</Typography>
								<Typography variant="body2" className={classes.heroSubtitle}>
									{language.tutorial_library_subtitle}
								</Typography>
							</div>
							{!isLoading && !hasLoadError && tutorialList.length > 0 && (
								<div className={classes.heroSearch}>
									<TextField
										className={classes.searchField}
										variant="outlined"
										size="small"
										value={searchQuery}
										onChange={handleSearchChange}
										placeholder={language.tutorial_search_placeholder}
										inputProps={{ 'aria-label': language.tutorial_search_placeholder }}
										InputProps={searchInputProps}
									/>
									<Typography variant="body2" className={classes.resultCount}>
										{filteredTutorials.length} {language.tutorial_video_count}
									</Typography>
								</div>
							)}
						</div>
					</Paper>

					{isLoading && (
						<Grid container spacing={2}>
							{[0, 1, 2, 3, 4, 5, 6, 7].map((index) => (
								<Grid item xs={12} sm={6} md={4} lg={3} key={index}>
									<Skeleton animation="wave" variant="rect" height={238} className={classes.skeletonCard} />
								</Grid>
							))}
						</Grid>
					)}

					{!isLoading && hasLoadError && (
						<Paper elevation={0} className={classes.statePanel}>
							<ErrorOutlineRoundedIcon className={classes.stateIcon} />
							<Typography variant="h6">{language.tutorial_load_failed}</Typography>
							<Button
								className={classes.retryButton}
								variant="contained"
								startIcon={<ReplayRoundedIcon />}
								onClick={handleRetry}
							>
								{language.tutorial_retry}
							</Button>
						</Paper>
					)}

					{!isLoading && !hasLoadError && tutorialList.length === 0 && (
						<Paper elevation={0} className={classes.statePanel}>
							<VideoLibraryRoundedIcon className={classes.stateIcon} />
							<Typography variant="h6">{language.no_data}</Typography>
						</Paper>
					)}

					{!isLoading && !hasLoadError && tutorialList.length > 0 && filteredTutorials.length === 0 && (
						<Paper elevation={0} className={classes.statePanel}>
							<SearchRoundedIcon className={classes.stateIcon} />
							<Typography variant="h6">{language.no_matches_found}</Typography>
							<Typography variant="body2" className={classes.stateMessage}>
								{language.try_different_keyword}
							</Typography>
						</Paper>
					)}

					{!isLoading && !hasLoadError && filteredTutorials.length > 0 && (
						<Grid container spacing={2}>
							{filteredTutorials.map((tutorial) => (
								<Grid item xs={12} sm={6} md={4} lg={3} key={`${tutorial.title}-${tutorial.url}`}>
									<TutorialCard tutorial={tutorial} language={language} onSelect={handleSelectTutorial} />
								</Grid>
							))}
						</Grid>
					)}
				</React.Fragment>
			)}
		</div>
	);
}
